import { useState, useCallback, useEffect } from 'react'
import {
  Table,
  TextInput,
  Select,
  Group,
  Paper,
  Text,
  Badge,
  ActionIcon,
  Pagination,
  Radio,
  Button,
  Affix,
  Transition,
  LoadingOverlay,
  Tooltip,
  Switch,
  SimpleGrid,
  RangeSlider,
  ThemeIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import {
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconFilter,
  IconFilterOff,
  IconDeviceFloppy,
  IconX,
  IconAlertTriangle,
  IconCheck,
  IconDatabase,
  IconTag,
  IconShieldCheck,
  IconEdit,
} from '@tabler/icons-react'
import { useCrimeCategoryCache, useCrimeCategoryBatchUpdate } from '@/hooks/useLabeling'
import type { CrimeCategoryCacheQuery, CrimeCategoryCacheRow } from '@/services/labeling.api'

type CrimeCategory = 'Criminal' | 'Traffic' | 'Other'

interface PendingEdit {
  originalCategory: string
  newCategory: CrimeCategory
  version: number
  isApproval?: boolean
}

function ConfidenceBadge({ value }: { value: number | null }) {
  if (value === null || value === undefined) return <Text size="xs" c="dimmed">N/A</Text>
  const color = value < 0.5 ? 'red' : value < 0.65 ? 'orange' : 'green'
  return (
    <Badge color={color} variant="light" size="sm">
      {(value * 100).toFixed(1)}%
    </Badge>
  )
}

function MethodCell({ value, finalCategory }: { value: string | null; finalCategory: string }) {
  if (!value) return <Text size="xs" c="dimmed">-</Text>
  const disagrees = value !== finalCategory
  return (
    <Badge
      size="xs"
      variant={disagrees ? 'filled' : 'light'}
      color={disagrees ? 'yellow' : 'gray'}
    >
      {value}
    </Badge>
  )
}

export function CrimeCategoryTable() {
  const [query, setQuery] = useState<CrimeCategoryCacheQuery>({
    page: 1,
    pageSize: 50,
    sortBy: 'confidence',
    sortOrder: 'asc',
  })

  const [searchInput, setSearchInput] = useState('')
  const [confidenceRange, setConfidenceRange] = useState<[number, number]>([0, 1])
  const [showFilters, { toggle: toggleFilters }] = useDisclosure(true)
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(new Map())

  const { data, isLoading, isFetching } = useCrimeCategoryCache(query)
  const batchUpdate = useCrimeCategoryBatchUpdate()

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const stats = data?.stats
  const totalPages = Math.ceil(total / query.pageSize)

  const hasPendingEdits = pendingEdits.size > 0

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingEdits.size > 0) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [pendingEdits.size])

  const handleSearch = useCallback(() => {
    if (hasPendingEdits) {
      if (!window.confirm(`You have ${pendingEdits.size} unsaved changes. Discard them?`)) return
      setPendingEdits(new Map())
    }
    setQuery(q => ({ ...q, page: 1, search: searchInput || undefined }))
  }, [searchInput, hasPendingEdits, pendingEdits.size])

  const handleSort = useCallback((column: string) => {
    setQuery(q => ({
      ...q,
      sortBy: column,
      sortOrder: q.sortBy === column && q.sortOrder === 'asc' ? 'desc' : 'asc',
    }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    if (hasPendingEdits) {
      if (!window.confirm(`You have ${pendingEdits.size} unsaved changes. Discard them?`)) return
      setPendingEdits(new Map())
    }
    setQuery(q => ({ ...q, page }))
  }, [hasPendingEdits, pendingEdits.size])

  const handleFilterChange = useCallback((partial: Partial<CrimeCategoryCacheQuery>) => {
    if (hasPendingEdits) {
      if (!window.confirm(`You have ${pendingEdits.size} unsaved changes. Discard them?`)) return
      setPendingEdits(new Map())
    }
    setQuery(q => ({ ...q, ...partial, page: 1 }))
  }, [hasPendingEdits, pendingEdits.size])

  const handleClearFilters = useCallback(() => {
    setSearchInput('')
    setConfidenceRange([0, 1])
    setQuery({
      page: 1,
      pageSize: query.pageSize,
      sortBy: 'confidence',
      sortOrder: 'asc',
    })
  }, [query.pageSize])

  const handleRadioChange = useCallback((row: CrimeCategoryCacheRow, newCategory: CrimeCategory) => {
    setPendingEdits(prev => {
      const next = new Map(prev)
      const existing = prev.get(row.id)
      if (newCategory === row.crime_category && !existing?.isApproval) {
        next.delete(row.id)
      } else {
        next.set(row.id, {
          originalCategory: row.crime_category,
          newCategory,
          version: row.version,
          isApproval: newCategory === row.crime_category,
        })
      }
      return next
    })
  }, [])

  const handleApprove = useCallback((row: CrimeCategoryCacheRow) => {
    setPendingEdits(prev => {
      const next = new Map(prev)
      if (prev.get(row.id)?.isApproval) {
        next.delete(row.id)
      } else {
        next.set(row.id, {
          originalCategory: row.crime_category,
          newCategory: row.crime_category as CrimeCategory,
          version: row.version,
          isApproval: true,
        })
      }
      return next
    })
  }, [])

  const handleSaveAll = useCallback(async () => {
    const updates = Array.from(pendingEdits.entries()).map(([id, edit]) => ({
      id,
      crime_category: edit.newCategory,
      version: edit.version,
    }))

    try {
      const result = await batchUpdate.mutateAsync(updates)

      if (result.totalConflicts > 0 || result.totalErrors > 0) {
        const remaining = new Map<string, PendingEdit>()
        for (const id of result.conflictIds) {
          const edit = pendingEdits.get(id)
          if (edit) remaining.set(id, edit)
        }
        for (const id of result.errorIds) {
          const edit = pendingEdits.get(id)
          if (edit) remaining.set(id, edit)
        }
        setPendingEdits(remaining)

        notifications.show({
          title: 'Partial save',
          message: `${result.totalUpdated} updated, ${result.totalConflicts} conflicts (version changed), ${result.totalErrors} errors`,
          color: 'yellow',
          icon: <IconAlertTriangle size={16} />,
        })
      } else {
        setPendingEdits(new Map())
        notifications.show({
          title: 'Labels saved',
          message: `${result.totalUpdated} label${result.totalUpdated !== 1 ? 's' : ''} updated successfully`,
          color: 'green',
          icon: <IconCheck size={16} />,
        })
      }
    } catch {
      notifications.show({
        title: 'Save failed',
        message: 'Could not save changes. Please try again.',
        color: 'red',
        icon: <IconX size={16} />,
      })
    }
  }, [pendingEdits, batchUpdate])

  const handleDiscardAll = useCallback(() => {
    setPendingEdits(new Map())
  }, [])

  const getDisplayCategory = useCallback((row: CrimeCategoryCacheRow): CrimeCategory => {
    const edit = pendingEdits.get(row.id)
    return (edit ? edit.newCategory : row.crime_category) as CrimeCategory
  }, [pendingEdits])

  const SortIcon = ({ column }: { column: string }) => {
    if (query.sortBy !== column) return null
    return query.sortOrder === 'asc'
      ? <IconSortAscending size={14} />
      : <IconSortDescending size={14} />
  }

  const sortableHeader = (label: string, column: string) => (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(column)}
    >
      <Group gap={4} wrap="nowrap">
        <Text size="xs" fw={600}>{label}</Text>
        <SortIcon column={column} />
      </Group>
    </Table.Th>
  )

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading && !data} />

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconDatabase size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Total Charges</Text>
            </Group>
            <Text size="xl" fw={700}>{stats.totalRows.toLocaleString()}</Text>
          </Paper>

          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="orange">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Low Confidence (&lt;0.65)</Text>
            </Group>
            <Text size="xl" fw={700} c="orange">
              {stats.lowConfidenceCount.toLocaleString()}
            </Text>
          </Paper>

          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="teal">
                <IconEdit size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Manual Overrides</Text>
            </Group>
            <Text size="xl" fw={700} c="teal">
              {stats.manualOverrideCount.toLocaleString()}
            </Text>
          </Paper>

          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb={4}>
              <ThemeIcon size="sm" variant="light" color="green">
                <IconShieldCheck size={14} />
              </ThemeIcon>
              <Text size="xs" c="dimmed" fw={500}>Ground Truth</Text>
            </Group>
            <Text size="xl" fw={700} c="green">
              {stats.groundTruthCount.toLocaleString()}
            </Text>
          </Paper>
        </SimpleGrid>
      )}

      {/* Filter Bar */}
      <Paper p="sm" radius="md" withBorder mb="md">
        <Group justify="space-between" mb={showFilters ? 'sm' : 0}>
          <Group gap="sm">
            <TextInput
              placeholder="Search charges..."
              leftSection={<IconSearch size={16} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              size="sm"
              style={{ minWidth: 280 }}
            />
            <Button size="sm" variant="light" onClick={handleSearch}>
              Search
            </Button>
          </Group>
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={toggleFilters} title="Toggle filters">
              {showFilters ? <IconFilterOff size={18} /> : <IconFilter size={18} />}
            </ActionIcon>
            <Button size="xs" variant="subtle" color="gray" onClick={handleClearFilters}>
              Clear All
            </Button>
          </Group>
        </Group>

        {showFilters && (
          <Group gap="md" align="flex-end">
            <Select
              label="Crime Category"
              placeholder="All"
              size="xs"
              clearable
              data={['Criminal', 'Traffic', 'Other']}
              value={query.crimeCategory ?? null}
              onChange={(val) => handleFilterChange({ crimeCategory: val || undefined })}
              style={{ minWidth: 140 }}
            />

            <Select
              label="Source"
              placeholder="All"
              size="xs"
              clearable
              data={['seed', 'ensemble', 'llm', 'historical', 'manual']}
              value={query.source ?? null}
              onChange={(val) => handleFilterChange({ source: val || undefined })}
              style={{ minWidth: 130 }}
            />

            <div style={{ minWidth: 200 }}>
              <Text size="xs" fw={500} mb={4}>Confidence Range</Text>
              <RangeSlider
                min={0}
                max={1}
                step={0.05}
                minRange={0.05}
                value={confidenceRange}
                onChange={setConfidenceRange}
                onChangeEnd={(val) => handleFilterChange({
                  confidenceMin: val[0] > 0 ? val[0] : undefined,
                  confidenceMax: val[1] < 1 ? val[1] : undefined,
                })}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.65, label: '0.65' },
                  { value: 1, label: '1' },
                ]}
                size="sm"
                label={(v) => `${(v * 100).toFixed(0)}%`}
              />
            </div>

            <Switch
              label="Ground Truth only"
              size="xs"
              checked={query.isGroundTruth === true}
              onChange={(e) => handleFilterChange({
                isGroundTruth: e.currentTarget.checked ? true : undefined,
              })}
            />

            <Switch
              label="Method disagreement"
              size="xs"
              checked={query.methodDisagreement === true}
              onChange={(e) => handleFilterChange({
                methodDisagreement: e.currentTarget.checked ? true : undefined,
              })}
            />
          </Group>
        )}
      </Paper>

      {/* Results info */}
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed">
          {isFetching ? 'Loading...' : `${total.toLocaleString()} results`}
          {query.page > 1 && ` (page ${query.page})`}
        </Text>
        <Select
          size="xs"
          value={String(query.pageSize)}
          onChange={(val) => handleFilterChange({ pageSize: parseInt(val!) })}
          data={['25', '50', '100']}
          style={{ width: 80 }}
        />
      </Group>

      {/* Data Table */}
      <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
        <Table striped highlightOnHover withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              {sortableHeader('Charge', 'charge_normalized')}
              <Table.Th style={{ minWidth: 260 }}>
                <Group gap={4}>
                  <IconTag size={14} />
                  <Text size="xs" fw={600}>Label</Text>
                </Group>
              </Table.Th>
              {sortableHeader('Confidence', 'confidence')}
              <Table.Th><Text size="xs" fw={600}>KNN</Text></Table.Th>
              <Table.Th><Text size="xs" fw={600}>Regex</Text></Table.Th>
              <Table.Th><Text size="xs" fw={600}>LLM</Text></Table.Th>
              {sortableHeader('Source', 'source')}
              <Table.Th><Text size="xs" fw={600}>GT</Text></Table.Th>
              {sortableHeader('Count', 'sample_count')}
              {sortableHeader('Updated', 'updated_at')}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 && !isLoading && (
              <Table.Tr>
                <Table.Td colSpan={10}>
                  <Text ta="center" c="dimmed" py="xl">No records found</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {rows.map((row) => {
              const edit = pendingEdits.get(row.id)
              const isEdited = !!edit
              const isApproval = edit?.isApproval === true
              const displayCategory = getDisplayCategory(row)
              const isLowConfidence = row.confidence !== null && row.confidence < 0.65
              const isAlreadyGroundTruth = row.is_ground_truth === true && row.confidence === 1.0

              return (
                <Table.Tr
                  key={row.id}
                  style={{
                    borderLeft: isEdited
                      ? `3px solid var(--mantine-color-${isApproval ? 'green' : 'blue'}-5)`
                      : undefined,
                    backgroundColor: isLowConfidence && !isEdited
                      ? 'var(--mantine-color-orange-0)'
                      : isApproval
                        ? 'var(--mantine-color-green-0)'
                        : isEdited
                          ? 'var(--mantine-color-blue-0)'
                          : undefined,
                  }}
                >
                  <Table.Td style={{ maxWidth: 350 }}>
                    <Tooltip label={row.charge_normalized} multiline maw={400}>
                      <Text size="sm" lineClamp={2}>{row.charge_normalized}</Text>
                    </Tooltip>
                  </Table.Td>

                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Radio.Group
                        value={displayCategory}
                        onChange={(val) => handleRadioChange(row, val as CrimeCategory)}
                      >
                        <Group gap={6}>
                          <Radio value="Criminal" label="Criminal" size="xs" />
                          <Radio value="Traffic" label="Traffic" size="xs" />
                          <Radio value="Other" label="Other" size="xs" />
                        </Group>
                      </Radio.Group>
                      {!isAlreadyGroundTruth && (
                        <Tooltip label={isApproval ? 'Undo approve' : 'Approve label as correct'}>
                          <ActionIcon
                            size="sm"
                            variant={isApproval ? 'filled' : 'subtle'}
                            color="green"
                            onClick={() => handleApprove(row)}
                          >
                            <IconCheck size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {isEdited && !isApproval && (
                        <Badge size="xs" variant="light" color="gray" td="line-through">
                          {edit!.originalCategory}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>

                  <Table.Td>
                    {isEdited ? (
                      <Tooltip label={`Was ${row.confidence !== null ? (row.confidence * 100).toFixed(1) + '%' : 'N/A'} — will be 100% after save`}>
                        <Badge color="green" variant="filled" size="sm">
                          100%
                        </Badge>
                      </Tooltip>
                    ) : (
                      <ConfidenceBadge value={row.confidence} />
                    )}
                  </Table.Td>

                  <Table.Td>
                    <MethodCell value={row.crime_category_knn} finalCategory={row.crime_category} />
                  </Table.Td>
                  <Table.Td>
                    <MethodCell value={row.crime_category_regex} finalCategory={row.crime_category} />
                  </Table.Td>
                  <Table.Td>
                    <MethodCell value={row.crime_category_llm} finalCategory={row.crime_category} />
                  </Table.Td>

                  <Table.Td>
                    {isEdited ? (
                      <Badge size="xs" variant="filled" color="teal">manual</Badge>
                    ) : row.source ? (
                      <Badge size="xs" variant="light" color={row.source === 'manual' ? 'teal' : 'blue'}>
                        {row.source}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">-</Text>
                    )}
                  </Table.Td>

                  <Table.Td>
                    {isEdited || row.is_ground_truth ? (
                      <ThemeIcon size="xs" variant={isEdited ? 'filled' : 'light'} color="green">
                        <IconShieldCheck size={12} />
                      </ThemeIcon>
                    ) : (
                      <Text size="xs" c="dimmed">-</Text>
                    )}
                  </Table.Td>

                  <Table.Td>
                    <Text size="xs">{row.sample_count ?? '-'}</Text>
                  </Table.Td>

                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {row.updated_at
                        ? new Date(row.updated_at).toLocaleDateString()
                        : '-'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination
            total={totalPages}
            value={query.page}
            onChange={handlePageChange}
            size="sm"
          />
        </Group>
      )}

      {/* Sticky Batch Save Toolbar */}
      <Affix position={{ bottom: 20, left: '50%' }} style={{ transform: 'translateX(-50%)' }}>
        <Transition mounted={hasPendingEdits} transition="slide-up">
          {(styles) => (
            <Paper
              style={styles}
              shadow="xl"
              radius="lg"
              p="sm"
              px="lg"
              withBorder
            >
              <Group gap="md">
                <Badge size="lg" variant="filled" color="blue" circle>
                  {pendingEdits.size}
                </Badge>
                <Text size="sm" fw={500}>
                  unsaved change{pendingEdits.size !== 1 ? 's' : ''}
                </Text>
                <Button
                  size="sm"
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSaveAll}
                  loading={batchUpdate.isPending}
                >
                  Save All
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  color="gray"
                  leftSection={<IconX size={16} />}
                  onClick={handleDiscardAll}
                  disabled={batchUpdate.isPending}
                >
                  Discard
                </Button>
              </Group>
            </Paper>
          )}
        </Transition>
      </Affix>
    </div>
  )
}
